import {GstStatus, ProrataPeriod, InfoformField} from "../common/types";
import {CostItem} from "./types";

export type Action<TAction extends string> = {type: TAction; id: string}
export type InputChange<TAction extends string, TNewValueType> = {to: TNewValueType} & Action<TAction>
export type SetGlCodeAction = InputChange<"set gl code", string>
export type SetJobCodeAction = InputChange<"set job code", string>
export type SetAccountNumberAction = InputChange<"set account number", string>
export type SetCostAction = InputChange<"set price", string>
export type SetInfoformFieldAction = InputChange<"set infoform field", number>
export type SetModeToPriceAction  = Action<"set mode to price">
export type SetModeToInfoformFieldAction  = Action<"set mode to infoform field"> & {defaultField:InfoformField}
export type SetGstStatusAction = InputChange<"set gst status", GstStatus>
export type SetProrataPeriodAction = InputChange<"set prorata period", ProrataPeriod>

// specifically for types which modify CostItem's.
export type CostItemAction = SetCostAction | SetInfoformFieldAction | SetGstStatusAction | SetJobCodeAction | SetGlCodeAction |
	SetCostAction | SetAccountNumberAction | SetProrataPeriodAction | SetModeToPriceAction | SetModeToInfoformFieldAction

export type CostItemAppAction = CostItemAction | {type: "this is not intended to be instantiated"} //for any of them

/**
 * If the item has an ID which matches the action, return the result of makeNewItem, otherwise return the item unchanged.
 */
const ifMatches = <TAction extends string>(action:Action<TAction>, item:CostItem, makeNewItem:(item:CostItem) => CostItem) =>
	item.id === action.id ? makeNewItem(item) : item

/**
 * If the item is the one specified by the action, then create a copy of the item, then update the property requested
 * by the action to the new value specified by the action.
 * @see ifMatches
 */
const modifyItemIfMatches = <TAction extends string, TNewValueType>(action: InputChange<TAction, TNewValueType>, item: CostItem, newProperties: {[key: string]: TNewValueType}): CostItem =>
	ifMatches(
		action,
		item,
		(item) => ({...item, ...newProperties})
	)

const updatePriceIfMatches = (action: SetCostAction) => (item: CostItem): CostItem =>
	modifyItemIfMatches(action, item, {price: action.to})

const updateInfoformFieldIfMatches = (action: SetInfoformFieldAction) => (item: CostItem): CostItem =>
	modifyItemIfMatches(action, item, {infoformField: action.to})

const changeModeToPriceIfMatches = (action: SetModeToPriceAction ) => (item: CostItem): CostItem =>
	ifMatches(
		action,
		item,
		(item) => ({...item, mode: "price", infoformField: 0})
	)

const changeModeToInfoformFieldIfMatches = (action: SetModeToInfoformFieldAction ) => (item: CostItem): CostItem =>
	ifMatches(
		action,
		item,
		(item) => ({...item, mode: "infoform field", infoformField: action.defaultField.id })
	)

const updateGstStatusIfMatches = (action: SetGstStatusAction) => (item: CostItem): CostItem =>
	modifyItemIfMatches(action, item, {gstStatus: action.to})

const updateGlCodeIfMatches = (action: SetGlCodeAction) => (item: CostItem): CostItem =>
	modifyItemIfMatches(action, item, {glCode: action.to})

const updateJobCodeIfMatches = (action: SetJobCodeAction) => (item: CostItem): CostItem =>
	modifyItemIfMatches(action, item, {jobCode: action.to})

const updateAccountNumberIfMatches = (action: SetAccountNumberAction) => (item: CostItem): CostItem =>
	modifyItemIfMatches(action, item, {accountNumber: action.to})

const updateProrataPeriodIfMatches = (action: SetProrataPeriodAction) => (item: CostItem): CostItem =>
	modifyItemIfMatches(action, item, {prorataPeriod: action.to})

export const items = (state:CostItem[] = [], action: CostItemAppAction): CostItem[] => {
	switch(action.type) {
		case "set price": return state.map(updatePriceIfMatches(action as SetCostAction))
		case "set infoform field": return state.map(updateInfoformFieldIfMatches(action as SetInfoformFieldAction))
		case "set mode to price": return state.map(changeModeToPriceIfMatches(action as SetModeToPriceAction))
		case "set mode to infoform field": return state.map(changeModeToInfoformFieldIfMatches(action as SetModeToInfoformFieldAction))
		case "set gst status": return state.map(updateGstStatusIfMatches(action as SetGstStatusAction))
		case "set gl code": return state.map(updateGlCodeIfMatches(action as SetGlCodeAction))
		case "set job code": return state.map(updateJobCodeIfMatches(action as SetJobCodeAction))
		case "set account number": return state.map(updateAccountNumberIfMatches(action as SetAccountNumberAction))
		case "set prorata period": return state.map(updateProrataPeriodIfMatches(action as SetProrataPeriodAction))
	}
	return state
}

export const ChangeActions = {
	price: (id: string, price: string): SetCostAction => { return {
		type: "set price",
		id: id,
		to: price
	}},

	priceField: (id: string, field: InfoformField): SetInfoformFieldAction => { return {
		type: "set infoform field",
		id: id,
		to: field.id
	}},

	modeToPrice: (id: string): SetModeToPriceAction => { return {
		type: "set mode to price",
		id: id
	}},

	modeToInfoformField: (id: string, defaultField:InfoformField): SetModeToInfoformFieldAction => { return {
		type: "set mode to infoform field",
		id: id,
		defaultField: defaultField
	}},

	gstStatus: (id: string, gstStatus: GstStatus): SetGstStatusAction => { return {
		type: "set gst status",
		id: id,
		to: gstStatus
	}},

	prorataPeriod: (id: string, prorataPeriod: ProrataPeriod): SetProrataPeriodAction => { return {
		type: "set prorata period",
		id: id,
		to: prorataPeriod
	}},

	itemCode: (id: string, itemCode: string): SetGlCodeAction => { return {
		type: "set gl code",
		id: id,
		to: itemCode
	}},

	jobCode: (id: string, jobCode: string): SetJobCodeAction => { return {
		type: "set job code",
		id: id,
		to: jobCode
	}},

	accountNumber: (id: string, accountNumber: string): SetAccountNumberAction => { return {
		type: "set account number",
		id: id,
		to: accountNumber
	}}
}



// WEBPACK FOOTER //
// ./src/membership3/pricing-option-form/standard-items/reducer.ts